"use client";

import { Action } from '@/lib/types';
import styles from './ActionCard.module.css';
import { ArrowRight, Check, X, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { useLanguage } from '@/lib/i18n/LanguageContext';

interface ActionCardProps {
    action: Action;
    onApprove?: (id: string) => void;
    onReject?: (id: string) => void;
}

export default function ActionCard({ action, onApprove, onReject }: ActionCardProps) {
    const { t } = useLanguage();
    const isAuto = action.mode === 'auto';
    const isHighRisk = action.risk === 'high';

    return (
        <div className={clsx('glass-card', styles.card, styles[action.mode])}>
            <div className={styles.header}>
                <div className={styles.badges}>
                    <span className={clsx(styles.modeBadge, styles[action.mode])}>
                        {action.mode}
                    </span>
                    <span className={clsx(styles.riskBadge, styles[action.risk])}>
                        {action.risk === 'low' ? <ShieldCheck size={12} /> : <AlertTriangle size={12} />}
                        {t(`action.risk.${action.risk}`)}
                    </span>
                </div>
                <span className={styles.date}>{action.date}</span>
            </div>

            <div className={styles.content}>
                <h3 className={styles.title}>{action.titleKey ? t(action.titleKey) : action.title}</h3>
                <p className={styles.description}>{action.descriptionKey ? t(action.descriptionKey) : action.description}</p>

                <div className={styles.changeBox}>
                    <div className={styles.changeVal}>
                        <span className={styles.label}>{t('action.label.current')}</span>
                        <span className={styles.val}>{action.currentValue}</span>
                    </div>
                    <ArrowRight size={16} className={styles.arrow} />
                    <div className={styles.changeVal}>
                        <span className={styles.label}>{t('action.label.proposed')}</span>
                        <span className={styles.valHighlight}>{action.proposedValue}</span>
                    </div>
                </div>

                <div className={styles.metrics}>
                    <div className={styles.metric}>
                        <span className={styles.metricLabel}>{t('action.metric.impact')}</span>
                        <span className={styles.metricValue}>{action.impact}</span>
                    </div>
                    <div className={styles.metric}>
                        <span className={styles.metricLabel}>{t('action.metric.why')}</span>
                        <span className={styles.metricValue}>{action.reason[0]}</span>
                    </div>
                </div>
            </div>

            {action.status === 'pending' && (
                <div className={styles.actions}>
                    <button className={styles.btnReject} onClick={() => onReject?.(action.id)}>
                        <X size={16} />
                        {t('action.reject')}
                    </button>
                    <button className={styles.btnApprove} onClick={() => onApprove?.(action.id)}>
                        <Check size={16} />
                        {t('action.approve')}
                    </button>
                </div>
            )}

            {action.status === 'executed' && (
                <div className={styles.executedBanner}>
                    <Check size={14} /> {t('action.status.executed')}
                </div>
            )}
        </div>
    );
}
